import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { apiFetch } from '../../services/api';

/**
 * RunDetailModal: displays execution details including status, timeline, input/output.
 *
 * Props:
 *   isOpen: boolean
 *   workflowId: UUID
 *   runId: string
 *   onClose: callback
 */
export const RunDetailModal = ({ isOpen, workflowId, runId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !runId) return;

    const fetchDetail = async () => {
      try {
        setLoading(true);
        // Canonical run-detail endpoint on the unified WorkflowController
        // surface — see refactor #36. RunDetail is keyed by runId only;
        // the workflowId is unused in the route but kept on the call
        // site so callers don't have to know about that detail.
        // Routed through the shared apiFetch so auth headers, the
        // single-flight token refresh, CSRF, and the session cookie are
        // applied consistently (no hand-rolled bearer / dead localStorage).
        const res = await apiFetch(`/api/v1/workflows/runs/${runId}`);
        setDetail(res.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch execution detail:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [isOpen, workflowId, runId]);

  if (!isOpen) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Execution Detail</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading && <div className="text-center text-gray-500">Loading...</div>}
          {error && <div className="bg-red-50 border border-red-200 rounded p-4 text-red-800">{error}</div>}

          {detail && (
            <>
              {/* Status Bar */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase">Status</p>
                  <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${getStatusColor(detail.status)} mt-1`}>
                    {detail.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase">Started</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {detail.startedAt ? new Date(detail.startedAt).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase">Completed</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {detail.completedAt ? new Date(detail.completedAt).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase">Run ID</p>
                  <p className="text-sm font-mono text-gray-900 mt-1">{detail.runId.substring(0, 12)}...</p>
                </div>
              </div>

              {/* Task States */}
              {detail.taskStates && detail.taskStates.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Task Timeline</h3>
                  <div className="space-y-2 border border-gray-200 rounded p-4 bg-gray-50">
                    {detail.taskStates.map((task, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-mono text-gray-700">{task.taskId}</span>
                          <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-800">{task.type}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                          {task.error && <span className="text-xs text-red-600">{task.error}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Input / Output */}
              <div className="grid grid-cols-2 gap-4">
                {detail.input && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Input</h3>
                    <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-auto max-h-64 text-gray-800">
                      {JSON.stringify(detail.input, null, 2)}
                    </pre>
                  </div>
                )}
                {detail.output && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Output</h3>
                    <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-auto max-h-64 text-gray-800">
                      {JSON.stringify(detail.output, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Failure */}
              {detail.failure && (
                <div className="bg-red-50 border border-red-200 rounded p-4">
                  <h3 className="text-sm font-semibold text-red-900 mb-2">Failure</h3>
                  <p className="text-sm text-red-800">{detail.failure}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
